import { Alert, Box, TextField, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/core-utils';
import { LoadingSpinner } from '@verantos-dev/shared-components';

import { useAllocationConfiguration } from './useAllocationConfiguration';

export const AllocationConfiguration = () => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  // TODO: cleanup with cp_annotation_show_dashboard_loading_spinner ff
  const showLoadingSpinner = useFeatureFlagSplit(
    'cp_annotation_show_dashboard_loading_spinner'
  ) as boolean;

  const {
    defaultAllocationMode,
    selectedAnnotators,
    trainingTotal,
    testTotal,
    validationTotal,
    dataSplitSummary,
    changeTrainingPercentage,
    changeTestPercentage,
    changeValidationPercentage,
    inputValues,
    setInputValues,
    isDataSplitSummaryLoading,
  } = useAllocationConfiguration();

  if (defaultAllocationMode) {
    return null;
  }

  const createAllocationTable = (
    title: string,
    field: 'trainingPercentage' | 'testPercentage' | 'validationPercentage',
    total: number,
    onChange: (value: string, annotatorId: string) => void
  ) => (
    <Box sx={{ mb: theme.spacing(4) }}>
      <Typography variant="h6" sx={{ mb: theme.spacing(2) }}>
        {title}
      </Typography>
      <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, width: '400px' }}>
        <Box
          id="table-header"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: theme.spacing(4),
            py: theme.spacing(2),
          }}
        >
          <Box id="header-annotator-cell">
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              Annotator
            </Typography>
          </Box>
          <Box id="header-percent-cell">
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              Percent (%)
            </Typography>
          </Box>
        </Box>

        <Box id="table-body">
          {selectedAnnotators.map((annotator) => {
            const inputValue = inputValues[annotator.id]?.[field] ?? '';
            return (
              <Box
                key={`${annotator.id}-${field}`}
                id="table-row"
                sx={{ display: 'flex', justifyContent: 'space-between', px: theme.spacing(4) }}
              >
                <Box id="row-annotator-cell" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body1" sx={{ textAlign: 'center' }}>
                    {annotator.label}
                  </Typography>
                </Box>
                <Box id="row-percent-cell" sx={{ p: theme.spacing(2) }}>
                  <TextField
                    value={inputValue}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setInputValues((prev: Record<string, Record<string, string>> | undefined) => {
                        const prevValue = prev ?? {};
                        return {
                          ...prevValue,
                          [annotator.id]: {
                            ...(prevValue[annotator.id] ?? {}),
                            [field]: newValue,
                          },
                        };
                      });
                      onChange(newValue, annotator.id);
                    }}
                    onBlur={() => {
                      const currentValue = inputValues[annotator.id]?.[field];
                      if (currentValue === '' || currentValue === '-') {
                        onChange('0', annotator.id);
                      }
                    }}
                    placeholder="0"
                    type="number"
                    inputMode="numeric"
                    size="small"
                    slotProps={{
                      htmlInput: {
                        min: 0,
                        max: 100,
                        step: 0.1,
                      },
                    }}
                    sx={{ width: 100 }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
        <Box
          id="table-footer"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: theme.spacing(4),
            py: theme.spacing(2),
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box id="footer-total-cell" sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              Total
            </Typography>
          </Box>
          <Box id="footer-total-cell" sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                fontWeight: 'bold',
                color: total === 100 ? theme.palette.success.main : theme.palette.error.main,
              }}
            >
              {total.toFixed(1)}%
            </Typography>
          </Box>
        </Box>
      </Box>

      {total !== 100 && (
        <Typography variant="body2" sx={{ color: 'error.main', mt: theme.spacing(1) }}>
          {formatMessage({
            id: 'adminProjects.allocation.allocationConfiguration.totalMustBe100Percent',
          })}
        </Typography>
      )}
    </Box>
  );
  if (showLoadingSpinner && isDataSplitSummaryLoading) {
    return (
      <Box
        id="allocation-configuration-loading"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '150px',
        }}
      >
        <LoadingSpinner id="allocationConfigurationLoading" />
      </Box>
    );
  }
  if (selectedAnnotators.length === 0) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {formatMessage({ id: 'adminProjects.allocation.allocationConfiguration.selectAnnotator' })}
      </Alert>
    );
  }
  return (
    <Box
      id="allocation-configuration"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        p: theme.spacing(6),
        gap: theme.spacing(3),
      }}
    >
      <Box id="validation-allocation-box">
        {createAllocationTable(
          formatMessage(
            { id: 'adminProjects.allocation.allocationConfiguration.validationAllocation' },
            { count: dataSplitSummary.validationCount }
          ),
          'validationPercentage',
          validationTotal,
          changeValidationPercentage
        )}
      </Box>
      <Box id="training-allocation-box">
        {createAllocationTable(
          formatMessage(
            { id: 'adminProjects.allocation.allocationConfiguration.trainingAllocation' },
            { count: dataSplitSummary.trainingCount }
          ),
          'trainingPercentage',
          trainingTotal,
          changeTrainingPercentage
        )}
      </Box>
      <Box id="test-allocation-box">
        {createAllocationTable(
          formatMessage(
            { id: 'adminProjects.allocation.allocationConfiguration.testAllocation' },
            { count: dataSplitSummary.testCount }
          ),
          'testPercentage',
          testTotal,
          changeTestPercentage
        )}
      </Box>
    </Box>
  );
};
